import { computed, toValue, type Ref } from "vue";
import { graphql as gql } from "@/generated";
import { useQuery } from "@urql/vue";

const GetSuperfanProductDocument = gql(/* GraphQL */ `
  query GetSuperfanProduct {
    getSuperfanProduct {
      ... on Product {
        id
        name
        description
        product_type
        is_active
        action_label
        benefit_descriptions
        localised_price {
          amount
          currency
          display_label
        }
        benefits {
          id
          benefit_type
          points_multiplier
        }
      }
      ... on ProductNotFoundError {
        error
        product_id
      }
      ... on NoChannelError {
        error
      }
    }
  }
`);

export interface SuperfanProductInfo {
  id: string;
  name: string;
  description?: string | null;
  product_type: string;
  is_active: boolean;
  action_label?: string | null;
  benefit_descriptions: string[];
  localised_price?: {
    amount: number;
    currency: string;
    display_label: string;
  } | null;
  benefits: Array<{
    id: string;
    benefit_type: string;
    points_multiplier?: number | null;
  }>;
}

/**
 * Composable to fetch the superfan product with useQuery.
 *
 * @param options.enabled - Ref or computed that controls when the query is executed
 * @returns superfanProduct, loading state, error, and hasSuperfanProduct
 */
export function useSuperfanProduct(options?: {
  enabled?: Ref<boolean> | (() => boolean);
}) {
  const {
    data: result,
    fetching: loading,
    error,
    executeQuery: refetch,
  } = useQuery({
    query: GetSuperfanProductDocument,
    variables: {},
    pause: () => !(toValue(options?.enabled) ?? true),
  });

  const superfanProduct = computed<SuperfanProductInfo | null>(() => {
    const response = result.value?.getSuperfanProduct;
    if (!response || "error" in response) return null;
    if ("id" in response) {
      return response as SuperfanProductInfo;
    }
    return null;
  });

  const hasSuperfanProduct = computed(
    () => superfanProduct.value !== null && superfanProduct.value.is_active,
  );

  return {
    superfanProduct,
    loading,
    hasSuperfanProduct,
    error,
    refetch,
  };
}
